(() => {
  /* =========================================================
    Helpers
  ========================================================== */
  const money = (cents) => {
    if (cents === null) return "TBD";
    const n = (Number(cents || 0) / 100);
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  const toCentsFromUsd = (usd) => Math.round(Number(usd || 0) * 100);

  const clampInt = (v, min, max) => {
    let n = Math.floor(Number(v || 0));
    if (!Number.isFinite(n)) n = min;
    if (n < min) n = min;
    if (max != null && n > max) n = max;
    return n;
  };

  const parseRange = (label) => {
    // "25–71 cases" or "25-71 cases"
    const s = String(label || "");
    const m = s.match(/(\d+)\s*[–-]\s*(\d+)/);
    if (!m) return null;
    return { min: Number(m[1]), max: Number(m[2]) };
  };

  const getMinCasesFromVariants = (variants) => {
    const mins = [];
    for (const v of variants) {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      const hit = opts.find(o => /cases/i.test(o));
      if (!hit) continue;
      const r = parseRange(hit);
      if (r) mins.push(r.min);
    }
    return mins.length ? Math.min(...mins) : 1;
  };

  const findCaseRangeVariant = (variants, cases) => {
    for (const v of variants) {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      const hit = opts.find(o => /cases/i.test(o));
      if (!hit) continue;
      const r = parseRange(hit);
      if (!r) continue;
      if (cases >= r.min && cases <= r.max) return v;
    }
    return null;
  };

  const getVariantIdFromUrl = () => {
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get("variant") || "";
    } catch (e) {
      return "";
    }
  };

  const getProductFormVariantId = (sectionId) => {
    if (!sectionId) return "";
    // Your theme uses: product_form_id = product-form-{{ section.id }}
    const form = document.querySelector(`#product-form-${sectionId}`);
    const idInput = form ? form.querySelector(`input[name="id"]`) : null;
    return idInput && idInput.value ? String(idInput.value) : "";
  };

  const getSelectedVariantId = (root) => {
    // 1) URL is best/most reliable for Shopify variant switching
    const fromUrl = getVariantIdFromUrl();
    if (fromUrl) return String(fromUrl);

    // 2) Then: product form hidden variant id inside this section
    const sectionId = root.getAttribute("data-section-id");
    const fromForm = getProductFormVariantId(sectionId);
    if (fromForm) return String(fromForm);

    // 3) Fallback: checked radio with data-variant-id (your swatch radios)
    const checkedWithId = document.querySelector(`input[type="radio"][data-variant-id]:checked`);
    if (checkedWithId?.getAttribute("data-variant-id")) return String(checkedWithId.getAttribute("data-variant-id"));

    return "";
  };

  const isAluminumProduct = (productTitle = "") => /aluminum/i.test(String(productTitle || ""));

  const getSizeFromText = (t = "") => {
    const s = String(t).toLowerCase();
    if (s.includes("8oz")) return "8oz";
    if (s.includes("12oz")) return "12oz";
    if (s.includes("16oz")) return "16oz";
    if (s.includes("20oz")) return "20oz";
    return "";
  };

  // Cups should ONLY show if product/options include "cup"
  const shouldShowCups = (productTitle, variants) => {
    const t = String(productTitle || "").toLowerCase();
    if (t.includes("cup")) return true;
    return variants.some(v =>
      [v.option1, v.option2, v.option3].some(x => /cup/i.test(String(x || "")))
    );
  };

  /* =========================================================
    DATA FROM YOUR UPLOADED SHEETS (exact)
    - Custom Label Water Product Specs. (2).xlsx
    - Freight By States (1).xlsx
  ========================================================== */

  // Setup fees
  const PET_SETUP_FEE_USD = 125; // PET Btl Pricing & Specs
  const AL_SETUP_FEE_USD = 175;  // Aluminum Btl Pricing & Specs (PSL)

  // Aluminum tier pricing (Bottle-Aluminum Ball (PSL), 16oz)
  const AL_PSL_TIERS = [
    { min: 70,  max: 139, pricePerCaseUsd: 35.99 },
    { min: 140, max: 209, pricePerCaseUsd: 33.99 },
    { min: 210, max: 279, pricePerCaseUsd: 31.99 },
    { min: 280, max: Infinity, pricePerCaseUsd: 29.99 },
  ];

  // DMW Freight out of Maryland (includes NY + NYC)
  const DMW_PER_PALLET = {
    AL:235, CT:175, DC:165, DE:185, FL:350, GA:205, IL:225, IN:225, KY:205,
    MA:185, MD:175, ME:235, MI:225, MO:305, MS:245, NC:175, NH:255, NJ:185,
    NY:185, NYC:365, OH:225, PA:185, RI:195, SC:175, TN:205, VA:175, VT:415,
    WI:310, WV:205
  };
  const DMW_LIFTGATE_FEE_USD = 20;
  const DMW_RESIDENTIAL_FEE_USD = 65;

  // Piedmont Springs Freight (Norcross, GA)
  const PIEDMONT = {
    "AL":{ "1":184.3128,  "2_6":184.3128 },
    "AR":{ "1":308.6807,  "2_6":240.501225 },
    "AZ*":{ "1":531.200525,"2_6":381.355525 },
    "CA*":{ "1":568.661775,"2_6":411.324525 },
    "CO*":{ "1":509.473,   "2_6":364.12335 },
    "CT":{ "1":352.13575, "2_6":287.7024 },
    "DC*":{ "1":345.392725,"2_6":218.7737 },
    "DE*":{ "1":368.6187, "2_6":244.996575 },
    "FL*":{ "1":215.58015,"2_6":215.58015 },
    "GA":{ "1":160,       "2_6":160 },
    "IA":{ "1":340.897375,"2_6":287.7024 },
    "ID*":{ "1":593.3862, "2_6":507.225325 },
    "IL*":{ "1":248.49315,"2_6":248.49315 },
    "IN":{ "1":282.457825,"2_6":224.018275 },
    "KS*":{ "1":323.6652, "2_6":287.7024 },
    "KY":{ "1":192.54105,"2_6":192.54105 },
    "LA*":{ "1":248.49315,"2_6":248.49315 },
    "MA*":{ "1":352.13575,"2_6":295.19465 },
    "MD*":{ "1":340.897375,"2_6":242.7489 },
    "ME":{ "1":430.05515,"2_6":295.19465 },
    "MI":{ "1":280.959375,"2_6":258.482625 },
    "MN*":{ "1":343.894275,"2_6":315.423725 },
    "MO*":{ "1":294.445425,"2_6":237.504325 },
    "MS":{ "1":226.26595,"2_6":188.055475 },
    "MT*":{ "1":593.3862,"2_6":507.225325 },
    "NC*":{ "1":195,"2_6":195 },
    "ND*":{ "1":999,"2_6":999 },
    "NE*":{ "1":408.327625,"2_6":274.965575 },
    "NH":{ "1":424.810575,"2_6":295.19465 },
    "NJ*":{ "1":280.959375,"2_6":256.984175 },
    "NM":{ "1":493.739275,"2_6":352.884975 },
    "NV*":{ "1":609.86915,"2_6":435.299725 },
    "NY*":{ "1":999,"2_6":999 },
    "OH":{ "1":291.448525,"2_6":226.26595 },
    "OK":{ "1":323.6652,"2_6":262.22875 },
    "OR":{ "1":580.649375,"2_6":435.299725 },
    "PA*":{ "1":343.894275,"2_6":295.19465 },
    "RI":{ "1":430.05515,"2_6":295.19465 },
    "SC":{ "1":195,"2_6":195 },
    "SD*":{ "1":999,"2_6":999 },
    "TN":{ "1":195,"2_6":195 },
    "TX":{ "1":408.327625,"2_6":287.7024 },
    "UT":{ "1":999,"2_6":999 },
    "VA":{ "1":195,"2_6":195 },
    "VT":{ "1":999,"2_6":999 },
    "WA":{ "1":999,"2_6":999 },
    "WI":{ "1":343.894275,"2_6":287.7024 }
  };
  const PIEDMONT_LIFTGATE_FEE_USD = 25;

  /* =========================================================
    NYC ZIP detection (DMW has NY vs NYC)
  ========================================================== */
  const isNYCZip = (zipRaw) => {
    const z = String(zipRaw || "").trim();
    if (!/^\d{5}/.test(z)) return false;
    const zip = Number(z.slice(0, 5));
    if (zip >= 10001 && zip <= 10282) return true; // Manhattan
    if (zip >= 10301 && zip <= 10314) return true; // Staten Island
    if (zip >= 10451 && zip <= 10475) return true; // Bronx
    if (zip >= 11101 && zip <= 11109) return true; // LIC/Queens
    if (zip >= 11201 && zip <= 11256) return true; // Brooklyn
    if (zip >= 11301 && zip <= 11697) return true; // Queens + Rockaways
    return false;
  };

  /* =========================================================
    Pallets MUST depend on freight source (from your PET sheet)
    - Piedmont: 8oz=120, 12oz=84, 16oz=72
    - DMW:      8oz=120, 12oz=96, 16oz=72, 16oz Bullet=72, etc.
    - Aluminum: 70 cases/pallet
  ========================================================== */
  const casesPerPallet = ({ productTitle, variantTitle, freightSource }) => {
    if (isAluminumProduct(productTitle)) return 70;

    const size = getSizeFromText(`${productTitle} ${variantTitle}`);

    if (freightSource === "DMW") {
      if (size === "12oz") return 96;
      if (size === "8oz") return 120;
      return 72;
    }

    // Piedmont
    if (size === "8oz") return 120;
    if (size === "12oz") return 84;
    return 72;
  };

  const calcPallets = (cases, cpp) => Math.max(0, Math.ceil((Number(cases) || 0) / (Number(cpp) || 1)));

  /* =========================================================
    Freight engine: Piedmont first, fallback to DMW
  ========================================================== */
  const piedmontLookupRateUsd = (state, pallets) => {
    const st = String(state || "").trim().toUpperCase();
    if (!st) return null;

    const row = PIEDMONT[st] || PIEDMONT[`${st}*`];
    if (!row) return null;

    const p = Math.max(0, Number(pallets || 0));
    if (p <= 0) return 0;

    let rate = null;
    if (p === 1) rate = Number(row["1"]);
    else if (p >= 2 && p <= 6) rate = Number(row["2_6"]);
    else return null; // >6 not defined -> do not guess

    if (!Number.isFinite(rate) || rate === 999) return null;
    return rate;
  };

  const dmwLookupRateUsd = (state, pallets, zip) => {
    const st = String(state || "").trim().toUpperCase();
    if (!st) return null;
    const p = Math.max(0, Number(pallets || 0));
    if (p <= 0) return 0;

    let key = st;
    if (st === "NY" && isNYCZip(zip)) key = "NYC";

    const perPallet = DMW_PER_PALLET[key];
    if (!Number.isFinite(perPallet)) return null;

    return perPallet * p;
  };

  const computeFreight = ({ productTitle, variantTitle, cases, state, zip, liftgate, limited }) => {
    const st = String(state || "").trim().toUpperCase();

    // Try Piedmont first (with Piedmont pallet rules)
    const cppPied = casesPerPallet({ productTitle, variantTitle, freightSource: "PIEDMONT" });
    const palletsPied = calcPallets(cases, cppPied);
    const piedRate = piedmontLookupRateUsd(st, palletsPied);

    if (piedRate != null) {
      let usd = piedRate;
      if (liftgate) usd += PIEDMONT_LIFTGATE_FEE_USD;
      // No Piedmont limited-access fee exists in your sheets -> don't invent it.
      return { source: "Piedmont (GA)", pallets: palletsPied, cents: toCentsFromUsd(usd) };
    }

    // Fallback to DMW (with DMW pallet rules)
    const cppDmw = casesPerPallet({ productTitle, variantTitle, freightSource: "DMW" });
    const palletsDmw = calcPallets(cases, cppDmw);
    const dmwRate = dmwLookupRateUsd(st, palletsDmw, zip);

    if (dmwRate != null) {
      let usd = dmwRate;
      if (liftgate) usd += DMW_LIFTGATE_FEE_USD;
      if (limited) usd += DMW_RESIDENTIAL_FEE_USD;

      const src = (st === "NY" && isNYCZip(zip)) ? "DMW (MD) — NYC Rate" : "DMW (MD)";
      return { source: src, pallets: palletsDmw, cents: toCentsFromUsd(usd) };
    }

    return { source: "Freight TBD", pallets: 0, cents: null };
  };

  /* =========================================================
    Aluminum pricing rules (exact from Aluminum sheet)
    - ONLY PSL tier prices exist in doc
    - DP: no price provided -> subtotal TBD
  ========================================================== */
  const aluminumPerCaseUsd = (cases) => {
    const c = Number(cases || 0);
    for (const t of AL_PSL_TIERS) {
      if (c >= t.min && c <= t.max) return t.pricePerCaseUsd;
    }
    return null;
  };

  const isAluminumDP = (variant) => {
    // Your UI shows "PSL" / "DP" (Label Type).
    // In Shopify variants, that label may be option1 or part of title.
    const s = String(variant?.option1 || variant?.title || "").toUpperCase();
    return s.includes("DP");
  };

  /* =========================================================
    Main binding
  ========================================================== */
  document.querySelectorAll("[data-quote-flow]").forEach((root) => {
    const panel = root.querySelector("[data-panel]");
    const btnEstimate = root.querySelector("[data-open-estimate]");
    const btnQuote = root.querySelector("[data-open-quote]");

    const casesEl = root.querySelector("[data-cases]");
    const cupsWrap = root.querySelector("[data-cups-wrap]");
    const cupsEl = root.querySelector("[data-cups]");

    const stateEl = root.querySelector("[data-state]");
    const zipEl = root.querySelector("[data-zip]");
    const liftgateEl = root.querySelector("[data-liftgate]");
    const limitedEl = root.querySelector("[data-limited]");

    const palletsEl = root.querySelector("[data-pallets]");
    const zoneEl = root.querySelector("[data-zone]");

    const subtotalEl = root.querySelector("[data-subtotal]");
    const freightEl = root.querySelector("[data-freight]");
    const totalEl = root.querySelector("[data-total]");

    const variantsJson = root.querySelector("[data-product-variants]");
    const variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];

    const form = root.querySelector("[data-quote-form]");
    const logo = root.querySelector("[data-logo]");
    const logoStatus = root.querySelector("[data-logo-status]");
    const submitBtn = root.querySelector("[data-submit-quote]");

    const hiddenVariant = root.querySelector("[data-hidden-variant]");
    const hiddenCases = root.querySelector("[data-hidden-cases]");
    const hiddenCups = root.querySelector("[data-hidden-cups]");
    const hiddenSubtotal = root.querySelector("[data-hidden-subtotal]");
    const hiddenFreight = root.querySelector("[data-hidden-freight]");
    const hiddenTotal = root.querySelector("[data-hidden-total]");
    const hiddenLiftgate = root.querySelector("[data-hidden-liftgate]");
    const hiddenLimited = root.querySelector("[data-hidden-limited]");

    const stateText = root.querySelector("[data-state-text]");
    const zipText = root.querySelector("[data-zip-text]");
    const streetText = root.querySelector("[data-street]");
    const cityText = root.querySelector("[data-city]");

    // optional estimator address fields (if present)
    const shipStreetEl = root.querySelector("[data-ship-street]");
    const shipCityEl = root.querySelector("[data-ship-city]");

    const productTitle = String(root.querySelector('input[name="contact[product]"]')?.value || "");

    // Show/hide cups properly
    const hasCups = shouldShowCups(productTitle, variants);
    if (cupsWrap) cupsWrap.hidden = !hasCups;

    // Determine min cases
    const hasCaseRanges = variants.some(v => [v.option1, v.option2, v.option3].some(x => /cases/i.test(String(x || ""))));
    const minCasesFromRanges = hasCaseRanges ? getMinCasesFromVariants(variants) : 1;
    const enforcedMin = isAluminumProduct(productTitle) ? 70 : minCasesFromRanges;

    if (casesEl) {
      casesEl.min = String(enforcedMin);
      if (Number(casesEl.value || 0) < enforcedMin) casesEl.value = String(enforcedMin);
    }

    const openPanel = () => {
      panel.hidden = false;
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    btnEstimate?.addEventListener("click", openPanel);
    btnQuote?.addEventListener("click", () => {
      openPanel();
      document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    });

    const validate = () => {
      const logoOk = !!(logo && logo.files && logo.files.length);
      if (logoStatus) logoStatus.textContent = logoOk ? `Selected: ${logo.files[0].name}` : "";

      const requiredOk = [...form.querySelectorAll("[required]")].every(el => String(el.value || "").trim().length > 0);

      // If subtotal is TBD (e.g. Aluminum DP has no pricing in doc) -> block submit
      const subtotalTbd = String(subtotalEl?.textContent || "").trim() === "TBD";
      submitBtn.disabled = !(logoOk && requiredOk) || subtotalTbd;
    };

    const recompute = () => {
      const cases = clampInt(casesEl?.value, enforcedMin, null);
      if (casesEl) casesEl.value = String(cases);

      const cups = clampInt(cupsEl?.value, 0, null);
      const st = String(stateEl?.value || "").trim().toUpperCase();
      const zip = String(zipEl?.value || "").trim();

      // --- Correct variant detection for YOUR THEME ---
      const selectedVariantId = getSelectedVariantId(root);
      const selectedVariant = variants.find(v => String(v.id) === String(selectedVariantId)) || null;

      let pickedVariant = null;
      let subtotalCents = null;

      // Pricing
      if (isAluminumProduct(productTitle)) {
        pickedVariant = selectedVariant;

        // If we cannot detect variant yet, do not guess PSL/DP
        if (!pickedVariant) {
          subtotalCents = null; // TBD
        } else if (isAluminumDP(pickedVariant)) {
          // DP pricing not present in your Aluminum sheet -> TBD (do not invent)
          subtotalCents = null;
        } else {
          // PSL
          const perCaseUsd = aluminumPerCaseUsd(cases);
          subtotalCents = toCentsFromUsd(perCaseUsd) * cases + toCentsFromUsd(AL_SETUP_FEE_USD);
        }
      } else if (hasCaseRanges) {
        // Shopify Case Range products: variant price is per-case in your CSV
        pickedVariant = findCaseRangeVariant(variants, cases);
        const priceCents = pickedVariant ? Number(pickedVariant.price) : 0;
        subtotalCents = (priceCents * cases) + toCentsFromUsd(PET_SETUP_FEE_USD);
      } else {
        // fallback
        pickedVariant = selectedVariant;
        const priceCents = pickedVariant ? Number(pickedVariant.price) : 0;
        subtotalCents = priceCents ? (priceCents * cases) : null;
      }

      // Freight (source-aware pallets + fallback + NYC zip)
      const freight = computeFreight({
        productTitle,
        variantTitle: pickedVariant ? pickedVariant.title : "",
        cases,
        state: st,
        zip,
        liftgate: !!liftgateEl?.checked,
        limited: !!limitedEl?.checked
      });

      // Totals
      const freightCents = freight.cents;
      const totalCents = (subtotalCents === null || freightCents === null) ? null : (subtotalCents + freightCents);

      // UI
      if (palletsEl) palletsEl.textContent = String(freight.pallets || 0);
      if (zoneEl) zoneEl.textContent = freight.source || "—";

      if (subtotalEl) subtotalEl.textContent = money(subtotalCents);
      if (freightEl) freightEl.textContent = money(freightCents);
      if (totalEl) totalEl.textContent = money(totalCents);

      // Hidden fields for submission
      if (hiddenVariant) hiddenVariant.value = pickedVariant ? (pickedVariant.title || "") : "";
      if (hiddenCases) hiddenCases.value = String(cases);
      if (hiddenCups) hiddenCups.value = String(cups);

      if (hiddenSubtotal) hiddenSubtotal.value = money(subtotalCents);
      if (hiddenFreight) hiddenFreight.value = money(freightCents);
      if (hiddenTotal) hiddenTotal.value = money(totalCents);

      if (hiddenLiftgate) hiddenLiftgate.value = liftgateEl?.checked ? "Yes" : "No";
      if (hiddenLimited) hiddenLimited.value = limitedEl?.checked ? "Yes" : "No";

      // Sync estimator to required form fields
      if (stateText && st) stateText.value = st;
      if (zipText && zip) zipText.value = zip;

      if (shipStreetEl && streetText && shipStreetEl.value && !streetText.value) streetText.value = shipStreetEl.value;
      if (shipCityEl && cityText && shipCityEl.value && !cityText.value) cityText.value = shipCityEl.value;

      validate();
    };

    // Recompute on your inputs
    ["input", "change"].forEach(evt => {
      casesEl?.addEventListener(evt, recompute);
      cupsEl?.addEventListener(evt, recompute);
      stateEl?.addEventListener(evt, recompute);
      zipEl?.addEventListener(evt, recompute);
      liftgateEl?.addEventListener(evt, recompute);
      limitedEl?.addEventListener(evt, recompute);
      shipStreetEl?.addEventListener(evt, recompute);
      shipCityEl?.addEventListener(evt, recompute);

      logo?.addEventListener(evt, validate);
      form?.addEventListener(evt, validate);
    });

    // Recompute when variants change (theme custom elements often dispatch change events)
    const sectionId = root.getAttribute("data-section-id");
    if (sectionId) {
      const radios = document.querySelector(`#variant-radios-${sectionId}`);
      const selects = document.querySelector(`#variant-selects-${sectionId}`);

      const hook = (el) => {
        if (!el) return;
        el.addEventListener("change", () => setTimeout(recompute, 0));
        // Some themes dispatch custom events – we listen broadly
        el.addEventListener("variant:change", () => setTimeout(recompute, 0));
      };
      hook(radios);
      hook(selects);
    }

    // Recompute if URL variant changes (back/forward or theme pushes history)
    window.addEventListener("popstate", () => setTimeout(recompute, 0));
    window.addEventListener("hashchange", () => setTimeout(recompute, 0));

    // Initial compute
    recompute();

    // Prevent submit if invalid
    form?.addEventListener("submit", (e) => {
      validate();
      if (submitBtn.disabled) e.preventDefault();
    });
  });
})();